import { emissionScopesForCommitment } from "./emissionScope";
import { percentAsEntered } from "./metricFormat";
import { EmissionFactorScope } from "../entities/emissionFactor";
import { categoryByTargetType, ecoLabelToString, environmentalCategoryToString, EnvironmentalMetricType, environmentalMetricTypeToUnit, EnvironmentalTargetType, environmentalTargetTypeToString, wasteDisposalDestinationToString } from "../entities/environmentalCommitment";
import { waterWithdrawnForCategory, WaterWithdrawn } from "../entities/environmentalReportSubmission";
import { EnvironmentalTargetDirection, EnvironmentalTargetMeasure, EnvironmentalTargetMeasuredBy, EnvironmentalTargetMethod } from "../entities/environmentalTarget";
// ---------------------------------------------------------------------------
// Metric dimensions and conversion
// ---------------------------------------------------------------------------
/**
 * Quantities may only be summed within a dimension. This exists because a
 * target scoped across categories would otherwise add litres of diesel to
 * kilowatt-hours of electricity to kilograms of LPG and present the result as
 * a compliance figure.
 */
export var EnvironmentalMetricDimension;
(function (EnvironmentalMetricDimension) {
    EnvironmentalMetricDimension["Volume"] = "volume";
    EnvironmentalMetricDimension["Mass"] = "mass";
    EnvironmentalMetricDimension["Energy"] = "energy";
    EnvironmentalMetricDimension["FreightDistance"] = "freight-distance";
    EnvironmentalMetricDimension["Ratio"] = "ratio";
    EnvironmentalMetricDimension["Count"] = "count";
})(EnvironmentalMetricDimension || (EnvironmentalMetricDimension = {}));
const DIMENSION_BY_METRIC = {
    [EnvironmentalMetricType.CubicMetres]: EnvironmentalMetricDimension.Volume,
    [EnvironmentalMetricType.Kilograms]: EnvironmentalMetricDimension.Mass,
    [EnvironmentalMetricType.Kilolitres]: EnvironmentalMetricDimension.Volume,
    [EnvironmentalMetricType.KilowattHours]: EnvironmentalMetricDimension.Energy,
    [EnvironmentalMetricType.Litres]: EnvironmentalMetricDimension.Volume,
    [EnvironmentalMetricType.NumberOfCylinders]: EnvironmentalMetricDimension.Count,
    [EnvironmentalMetricType.Percentage]: EnvironmentalMetricDimension.Ratio,
    [EnvironmentalMetricType.TonneKilometres]: EnvironmentalMetricDimension.FreightDistance,
    [EnvironmentalMetricType.Tonnes]: EnvironmentalMetricDimension.Mass
};
/** Factor converting one unit of the metric into its dimension's base unit
 *  (litres for volume, kilograms for mass, kWh for energy). */
const BASE_UNIT_FACTOR = {
    [EnvironmentalMetricType.CubicMetres]: 1000,
    [EnvironmentalMetricType.Kilograms]: 1,
    [EnvironmentalMetricType.Kilolitres]: 1000,
    [EnvironmentalMetricType.KilowattHours]: 1,
    [EnvironmentalMetricType.Litres]: 1,
    [EnvironmentalMetricType.NumberOfCylinders]: 1,
    [EnvironmentalMetricType.Percentage]: 1,
    [EnvironmentalMetricType.TonneKilometres]: 1,
    [EnvironmentalMetricType.Tonnes]: 1000
};
export const getEnvironmentalMetricDimension = (metricType) => DIMENSION_BY_METRIC[metricType];
export const areEnvironmentalMetricsConvertible = (a, b) => getEnvironmentalMetricDimension(a) === getEnvironmentalMetricDimension(b);
/**
 * Converts within a dimension (L↔m³, kg↔t). Returns `undefined` for
 * cross-dimension pairs rather than a wrong number — callers must decide how
 * to surface the incompatibility.
 */
export const convertEnvironmentalQuantity = (value, from, to) => {
    if (!areEnvironmentalMetricsConvertible(from, to)) {
        return undefined;
    }
    if (from === to) {
        return value;
    }
    return (value * BASE_UNIT_FACTOR[from]) / BASE_UNIT_FACTOR[to];
};
// ---------------------------------------------------------------------------
// Scope rules
// ---------------------------------------------------------------------------
/**
 * Whether this target's scope resolves to one coherent unit.
 *
 * Holds for *every* target, whatever its method. Percentage targets used to be
 * exempt on the grounds that a ratio is unitless — but the ratio is computed by
 * summing quantities, and `sumQuantities` silently skips leaves it cannot
 * convert. A "Recycled Component across all Waste" target therefore dropped
 * every litres row from both numerator and denominator and reported the
 * tonnes-only figure under a label that said all Waste. The arithmetic was
 * unitless; the rows it was computed from were not.
 *
 * With the rule applied uniformly, a target's scope reports in exactly one
 * unit, and no sum can ever silently exclude a row. The cost is that a scope
 * spanning units must be split into one target per coherent category.
 */
export const environmentalTargetScopeIsCoherent = (target, commitments) => {
    // Suspended for emissions, and only for emissions. The rule exists because
    // there was no common denominator across leaves — and the emission factor IS
    // that denominator: a litre of diesel and a tonne of steel both convert to
    // tCO₂e, which is the whole reason a footprint can be totalled at all. Every
    // other measure keeps the rule.
    // Suspended for emissions and for value, and only for those two: each IS a
    // common denominator across leaves — the emission factor, and the invoice.
    if (target.measure === EnvironmentalTargetMeasure.Emissions ||
        target.measure === EnvironmentalTargetMeasure.Value) {
        return true;
    }
    // An empty scope is orphaned, not incoherent — reported separately so the
    // user is not told to narrow a scope that has nothing in it.
    const scoped = resolveEnvironmentalTargetCommitments(target, commitments);
    return (scoped.length === 0 ||
        deriveEnvironmentalTargetMetricType(target, commitments) !== undefined);
};
/**
 * Whether a leaf emits in any of the GHG scopes the target measures.
 *
 * Only an emissions target asks: a native-quantity target is not measured in
 * tCO₂e at all. An emissions target with no scopes listed means the whole
 * footprint — every scope, but still only leaves that emit AT ALL. A leaf
 * with no factor (Water Usage, an unmapped sub-category) can only ever hold
 * an emissions figure at zero, and pulling it into scope makes an all-water
 * project read 0 tCO₂e against its cap — perfect compliance reported from a
 * question the leaf cannot answer — instead of orphaned. Named scopes always
 * excluded such leaves (an empty scope set matches nothing); the wildcard now
 * applies the same rule.
 *
 * Membership is by EVERY scope the leaf contributes to, not its primary
 * factor's — see {@link emissionScopesForCommitment}. Filtering on the primary
 * would drop every fuel from a Scope 3 target while its well-to-pump share is
 * one of the biggest things in it.
 */
export const commitmentEmitsInTargetScopes = (target, commitment) => {
    if (target.measure !== EnvironmentalTargetMeasure.Emissions) {
        return true;
    }
    const scopes = emissionScopesForCommitment({
        category: commitment.category,
        sub_category: commitment.subCategory,
        target_type: commitment.targetType
    });
    if (target.emissionScopes == null || target.emissionScopes.length === 0) {
        return scopes.size > 0;
    }
    return target.emissionScopes.some((scope) => scopes.has(scope));
};
/** What a scope is counted in. Saying nothing means what everything meant
 *  before emissions existed. */
const measureOf = (scope) => scope.measure ?? EnvironmentalTargetMeasure.NativeQuantity;
/** The GHG scopes a scope spans. Absent — and empty — mean all three, which is
 *  the same wildcard `category` is. */
const emissionScopesOf = (scope) => scope.emissionScopes && scope.emissionScopes.length > 0
    ? scope.emissionScopes
    : [
        EmissionFactorScope.Scope1,
        EmissionFactorScope.Scope2,
        EmissionFactorScope.Scope3
    ];
/**
 * Whether `scope` contains `inner` — an absent category or sub-category on the
 * OUTER scope is a wildcard, exactly as it is when a target resolves its
 * leaves.
 *
 * The asymmetry is the point: a framework entry scoped to all Materials covers
 * a target scoped to Wood/Timber, and never the other way round. That is how a
 * policy stating "report your materials" is answered by the target the user
 * draws inside it — see `resolveEnvironmentalTargetCommitments`, which applies
 * the same rule one level down, from a target to the category leaves.
 *
 * ─ The measure is not a narrowing, it is a different question ─
 *
 * Two scopes over the same categories still measure different things if one
 * counts litres and the other counts tCO₂e, so the measures must MATCH; there
 * is no wildcard and no direction. This is the fix for a live bug: the SA DIT
 * "Scope 1 & 2 emissions" obligation is scoped to Energy, and while only the
 * categories were compared **any** Energy target answered it — a fuel-volume
 * target made the policy banner disappear.
 *
 * The GHG scopes then narrow within the measure, the same way the category
 * does: absent (or empty) is every scope, and an outer scope covers an inner
 * one when the inner's scopes are a subset of its own. So a Scope 1 & 2
 * obligation is not answered by a Scope 3 target — the same class of bug, one
 * level down.
 */
export const environmentalScopeCovers = (scope, inner) => {
    if (measureOf(scope) !== measureOf(inner))
        return false;
    const outerScopes = emissionScopesOf(scope);
    if (!emissionScopesOf(inner).every((emissionScope) => outerScopes.includes(emissionScope))) {
        return false;
    }
    return (
    // An absent type on the OUTER scope is the same wildcard the category and
    // sub-category already are. An absent type on the INNER one is wider than
    // anything a framework entry can state, so it is covered by nothing.
    (scope.targetType == null ||
        (inner.targetType != null && scope.targetType === inner.targetType)) &&
        (scope.category == null || scope.category === inner.category) &&
        (scope.subCategory == null || scope.subCategory === inner.subCategory));
};
/**
 * Resolves a target's scope against the project's currently enabled leaves.
 * Scope is dynamic by design: enabling a new leaf inside an existing target's
 * scope brings it into that target's numerator and denominator.
 */
export const resolveEnvironmentalTargetCommitments = (target, commitments) => commitments.filter((commitment) => 
// Absent type = every type, the same wildcard as category. Only an
// emissions target can leave it out; nothing else would have a coherent
// unit to state its figure in.
(target.targetType == null ||
    commitment.targetType === target.targetType) &&
    (target.category == null || commitment.category === target.category) &&
    (target.subCategory == null ||
        commitment.subCategory === target.subCategory) &&
    commitmentEmitsInTargetScopes(target, commitment));
/**
 * A target whose scope resolves to nothing — because Category Set-up switched
 * its categories off. Such a target must render as "no data" everywhere, never
 * as 0%: a diversion target reporting 0% because its scope was disabled is a
 * false compliance failure.
 */
export const isEnvironmentalTargetOrphaned = (target, commitments) => resolveEnvironmentalTargetCommitments(target, commitments).length === 0;
/**
 * The metric every leaf in scope shares, or `undefined` when they disagree
 * (or the scope is empty). This is the scope's *native* unit — what the supply
 * chain reports in — and the default the target is expressed in.
 */
export const deriveEnvironmentalTargetMetricType = (target, commitments) => {
    const scoped = resolveEnvironmentalTargetCommitments(target, commitments);
    if (scoped.length === 0) {
        return undefined;
    }
    const [first, ...rest] = scoped;
    return rest.every((commitment) => areEnvironmentalMetricsConvertible(commitment.metricType, first.metricType))
        ? first.metricType
        : undefined;
};
/**
 * Which way is good for this target — read through here, never off a field.
 *
 *   Emissions                      →  DoNotExceed (always)
 *   Baseline to target             →  the stored `baselineFactor`
 *   anything else                  →  ReachOrExceed
 *
 * A figure counted in tCO₂e is something to hold DOWN, whatever the row says:
 * validation refuses a `≥` factor on an emissions target, and this ignores one
 * that slipped through, so an emissions target can never be scored as a floor
 * that rewards the project for emitting more.
 *
 * Baseline to target is the ONE place the direction is stored, because it is
 * the one place the user chooses it (the "Baseline factor" select). There is
 * deliberately still no general `target_direction` column: a direction stored
 * where nobody chooses it is a second place for one fact to live. A factor
 * missing from an old row reads as `≤`, which is what every baseline target
 * meant before the factor existed.
 *
 * Read one layer up by `capPace` / `absolutePace`, which is where the direction
 * changes an arithmetic rather than a label — see the note on
 * {@link EnvironmentalTargetDirection}.
 */
export const directionForTarget = (target) => {
    if (target.measure === EnvironmentalTargetMeasure.Emissions) {
        return EnvironmentalTargetDirection.DoNotExceed;
    }
    if (target.targetMethod === EnvironmentalTargetMethod.BaselineToTarget) {
        return target.baselineFactor ?? EnvironmentalTargetDirection.DoNotExceed;
    }
    return EnvironmentalTargetDirection.ReachOrExceed;
};
/**
 * The measures a target may hold.
 *
 * `Value` is Materials + Percentage only. It is the one measure whose figures
 * come from a field that is not always collected, so offering it on a scope
 * whose rows carry no value would produce a target that can never be scored.
 * It is kept off `FixedValue` and `BaselineToTarget` on purpose: both derive
 * `measuredBy` rather than choosing it, and a dollar target on either would
 * have to break that invariant for something nobody has asked for.
 *
 * Emissions is listed whatever the scope; the drawer disables it with a reason
 * when the project has no emitting leaf, because hiding it would say "this
 * product has no emissions targets" when the truth is about this project.
 */
export const measureOptionsForTarget = (target) => [
    EnvironmentalTargetMeasure.NativeQuantity,
    EnvironmentalTargetMeasure.Emissions,
    ...(target.targetType === EnvironmentalTargetType.Materials &&
        target.targetMethod === EnvironmentalTargetMethod.Percentage
        ? [EnvironmentalTargetMeasure.Value]
        : [])
];
/**
 * The measure a target is saved with: its own when that is still on offer,
 * otherwise the default.
 *
 * The counterpart to {@link deriveEnvironmentalTargetMeasuredBy}, and needed
 * for the same reason — switching a Value target's method to Fixed value, or
 * its type away from Materials, must not leave a dollar measure behind on a
 * target that cannot hold one and that nothing would ever score.
 */
export const deriveEnvironmentalTargetMeasure = (target) => {
    const options = measureOptionsForTarget(target);
    return target.measure && options.includes(target.measure)
        ? target.measure
        : EnvironmentalTargetMeasure.NativeQuantity;
};
/**
 * The Measured By values a target may hold. A single entry means the field is
 * derived and shown read-only; more than one means the drawer offers a choice.
 *
 * Only a Percentage target chooses, and only Waste and Water have a share to
 * choose: Waste by disposal destination, Water by source category.
 */
export const measuredByOptionsForTarget = (target) => {
    if (target.targetMethod !== EnvironmentalTargetMethod.Percentage) {
        return [EnvironmentalTargetMeasuredBy.ShareOfQuantities];
    }
    if (target.measure === EnvironmentalTargetMeasure.Emissions) {
        return [EnvironmentalTargetMeasuredBy.RecycledPercentage];
    }
    // A money-weighted share has exactly one thing worth measuring: whether the
    // product carries a label. Weighting a recycled % by dollars, or a disposal
    // destination, answers no question anyone has asked.
    if (target.measure === EnvironmentalTargetMeasure.Value) {
        return [EnvironmentalTargetMeasuredBy.EcoLabelShare];
    }
    switch (target.targetType) {
        case EnvironmentalTargetType.Waste:
            return [
                EnvironmentalTargetMeasuredBy.RecycledPercentage,
                EnvironmentalTargetMeasuredBy.DestinationShare
            ];
        case EnvironmentalTargetType.WaterUsage:
            return [
                EnvironmentalTargetMeasuredBy.RecycledPercentage,
                EnvironmentalTargetMeasuredBy.SourceShare
            ];
        default:
            return [EnvironmentalTargetMeasuredBy.RecycledPercentage];
    }
};
/**
 * The Measured By a target is saved with: its own choice when that choice is
 * still on offer, otherwise the first option — so changing the method or the
 * type can never leave a share mode behind on a target that cannot hold one.
 *
 * Single source for the drawer's Measured By field, the value stored on save,
 * and the framework bridges (which carry no choice and so get the default).
 */
export const deriveEnvironmentalTargetMeasuredBy = (target) => {
    const options = measuredByOptionsForTarget(target);
    return target.measuredBy && options.includes(target.measuredBy)
        ? target.measuredBy
        : options[0];
};
/**
 * The water categories a Source share may pick: every Water Usage category
 * whose water is WITHDRAWN. Discharge is water leaving the site, not water
 * used, so it is neither a source nor part of the share's denominator.
 */
export const SOURCE_SHARE_CATEGORIES = categoryByTargetType[EnvironmentalTargetType.WaterUsage].filter((category) => waterWithdrawnForCategory(category) === WaterWithdrawn.Withdrawn);
/**
 * Whether a reported item's recycled % is asked for.
 *
 * - **Materials: always.** The Materials analytics tile and both dashboards
 *   show a weighted recycled %, whatever targets exist.
 * - **Water and Waste: only when a Recycled Component target covers the item.**
 *   Nothing else reads the figure — the dashboards' recycled water counts the
 *   Reused on site category, and waste recovery counts the destination — so
 *   without such a target it is data nobody sees.
 * - Energy and Transport: never; their items have no recycled share.
 *
 * A target added after months were reported sees no recycled % on Water items
 * from those months (they read as not measured), and the default share on
 * Waste items (100 on Recycled, 0 elsewhere). The target drawer says so.
 */
export const isRecycledPctCollected = (commitment, targets) => {
    switch (commitment.targetType) {
        case EnvironmentalTargetType.Materials:
            return true;
        case EnvironmentalTargetType.WaterUsage:
        case EnvironmentalTargetType.Waste:
            return targets.some((target) => isRecycledComponentTarget(target) &&
                resolveEnvironmentalTargetCommitments(target, [commitment]).length > 0);
        default:
            return false;
    }
};
/**
 * Whether a reported material item's invoice value is asked for.
 *
 * Only when a Value target covers the item — the burdensome half of the
 * `isRecycledPctCollected` split. A subcontractor disclosing what it paid for
 * steel is commercially sensitive and needs the invoice to hand, so it is not
 * asked of projects with no target that reads it. The eco-label, by contrast,
 * IS asked on every materials row: it is an intrinsic property of the product,
 * it is the thing the client asked for first, and it is what gives a target
 * created next quarter any history to score.
 *
 * Prospective only, and deliberately so. A target added in month 8 leaves
 * seven months of unpriced rows behind it that no rule here can reach — which
 * is why `EnvironmentalTargetAttainment.unpricedRowCount` exists and has to be
 * shown wherever the figure is.
 */
export const isMaterialValueCollected = (commitment, targets) => commitment.targetType === EnvironmentalTargetType.Materials &&
    targets.some((target) => isEcoLabelShareTarget(target) &&
        resolveEnvironmentalTargetCommitments(target, [commitment]).length > 0);
/**
 * A money-weighted Percentage target — an eco-label share by construction,
 * since `measuredByOptionsForTarget` offers nothing else for `Value`.
 */
export const isEcoLabelShareTarget = (target) => target.measure === EnvironmentalTargetMeasure.Value &&
    target.targetMethod === EnvironmentalTargetMethod.Percentage;
/** A Percentage target measured by the row's recycled %. */
export const isRecycledComponentTarget = (target) => target.measure !== EnvironmentalTargetMeasure.Emissions &&
    target.targetMethod === EnvironmentalTargetMethod.Percentage &&
    deriveEnvironmentalTargetMeasuredBy(target) ===
        EnvironmentalTargetMeasuredBy.RecycledPercentage;
/** Whether a target carries the waste row filters (Hazardous, Destinations). */
export const targetTakesWasteFilters = (target) => target.measure !== EnvironmentalTargetMeasure.Emissions &&
    target.targetType === EnvironmentalTargetType.Waste;
const listLabel = (labels, noun) => labels.length > 2 ? `${labels.length} ${noun}` : labels.join(", ");
/**
 * The extra part of a scope label a target's row filters and share picks add —
 * `Non-haz, Recycled, Reuse` — or `undefined` when it has none, so a target
 * without them labels exactly as it always did.
 *
 * `short` is what a cell shows (more than two picks collapse to a count);
 * `full` names every pick, for a tooltip or an export. One wording, shared by
 * the set-up table, the analytics tables and the PDF.
 */
export const environmentalTargetFilterLabel = (target) => {
    const shortParts = [];
    const fullParts = [];
    if (target.wasteHazardous != null) {
        const hazard = target.wasteHazardous ? "Haz" : "Non-haz";
        shortParts.push(hazard);
        fullParts.push(hazard);
    }
    const destinations = (target.wasteDisposalDestinations ?? []).map(wasteDisposalDestinationToString);
    if (destinations.length > 0) {
        shortParts.push(listLabel(destinations, "destinations"));
        fullParts.push(destinations.join(", "));
    }
    const sources = target.measuredBy === EnvironmentalTargetMeasuredBy.SourceShare
        ? (target.shareCategories ?? []).map(environmentalCategoryToString)
        : [];
    if (sources.length > 0) {
        shortParts.push(listLabel(sources, "sources"));
        fullParts.push(sources.join(", "));
    }
    // Only when the target names a subset. An eco-label target accepting ANY
    // approved label has nothing to caption — the scope line already says
    // Materials, and "any label" adds no narrowing for the reader to check.
    const labels = target.measuredBy === EnvironmentalTargetMeasuredBy.EcoLabelShare
        ? (target.ecoLabels ?? []).map(ecoLabelToString)
        : [];
    if (labels.length > 0) {
        shortParts.push(listLabel(labels, "labels"));
        fullParts.push(labels.join(", "));
    }
    return shortParts.length > 0
        ? { full: fullParts.join(", "), short: shortParts.join(", ") }
        : undefined;
};
/**
 * The head of a target's scope label: its EnviroTrack type, or "All Types"
 * when it names none.
 *
 * Only an emissions target can leave the type out — "total Scope 3" spans
 * Waste, Materials and Transport — and every surface that labels a scope has to
 * say so rather than render an empty cell. One wording, so the tables, the
 * drawer and the compliance rows cannot disagree.
 */
export const environmentalTargetTypeLabel = (targetType) => targetType ? environmentalTargetTypeToString(targetType) : "All Types";
// ---------------------------------------------------------------------------
// Emissions thresholds and reductions
// ---------------------------------------------------------------------------
/** tCO₂e — the unit an emissions target states its threshold in. Not an
 *  `EnvironmentalMetricType`: `Tonnes` there means tonnes of material. */
export const EMISSIONS_UNIT = "tCO₂e";
/** Whether the target is a change against a stated baseline. */
export const isBaselineToTarget = (target) => target.targetMethod === EnvironmentalTargetMethod.BaselineToTarget;
/**
 * The threshold a Baseline to target target comes to:
 *
 *   ≤ (DoNotExceed)    baseline × (1 − change%)   "≤ Baseline − 20%"
 *   ≥ (ReachOrExceed)  baseline × (1 + change%)   "≥ Baseline + 10%"
 *
 * The user states the figures they actually know — the baseline, the size of
 * the change (always positive) and its direction — and this is the only place
 * they become a threshold. Storing the threshold instead would make the
 * figures on screen derived FROM it, and a rounding difference would put
 * numbers on one row that do not reconcile.
 */
export const baselineTargetThreshold = (baselineValue, changePct, direction) => direction === EnvironmentalTargetDirection.ReachOrExceed
    ? baselineValue * (1 + changePct / 100)
    : baselineValue * (1 - changePct / 100);
/**
 * The figure a target is actually scored against, whatever it was entered as.
 *
 * For Baseline to target that is the derived threshold; for everything else it
 * is `targetValue` as typed. **Every consumer must go through this** —
 * attainment, pace and the tables — because a baseline target's `targetValue`
 * is a PERCENTAGE, and reading it as a quantity would measure a project
 * emitting hundreds of tonnes against a threshold of 20.
 *
 * `undefined` when a baseline target has no usable baseline: there is no
 * threshold yet, which is not the same as a threshold of zero.
 */
export const environmentalTargetThreshold = (target) => {
    if (!isBaselineToTarget(target))
        return target.targetValue;
    const baseline = target.baselineValue;
    if (baseline == null || baseline <= 0)
        return undefined;
    return baselineTargetThreshold(baseline, target.targetValue, directionForTarget(target));
};
/** The unit a target's figures are stated in — tCO₂e for emissions, otherwise
 *  the scope's own metric. */
export const environmentalTargetUnit = (target) => target.measure === EnvironmentalTargetMeasure.Emissions
    ? EMISSIONS_UNIT
    : target.metricType
        ? environmentalMetricTypeToUnit(target.metricType)
        : "";
/**
 * The baseline a Baseline to target target is measured from, formatted — and
 * `undefined` for every other method, which has none.
 *
 * This is the Baseline column, and it is what makes the percentage beside it
 * mean anything: the same physical cut reads as 5%, 8% or 13% depending only on
 * what it is measured from, so the two are always on screen together.
 */
export const formatEnvironmentalTargetBaseline = (target) => {
    if (!isBaselineToTarget(target) || target.baselineValue == null) {
        return undefined;
    }
    const unit = environmentalTargetUnit(target);
    return `${target.baselineValue.toLocaleString("en-AU")}${unit ? ` ${unit}` : ""}`;
};
/**
 * Table-cell value: percentage, quantity with its derived unit, or a dash.
 *
 * The one rendering of a target's threshold, shared by project set-up and the
 * analytics Targets tables so the same commitment cannot read two ways. Both
 * used to format it themselves and disagreed — set-up showed "5,000 t" where
 * analytics showed "5,000.0 t" for the same target.
 *
 * A threshold is a number somebody typed, so it is shown as typed: grouped,
 * with whatever decimals were entered and no padding. That is the same rule
 * {@link percentAsEntered} applies, and the opposite of the fixed 1dp that
 * `quantity` gives *measured* values — a measurement's precision is a property
 * of the measurement, a commitment's is a property of the commitment.
 *
 * Takes only the three fields it reads so analytics can supply a `metricType`
 * resolved from its own attainment payload.
 */
export const formatEnvironmentalTargetValue = (target) => {
    if (target.targetMethod === EnvironmentalTargetMethod.ReportOnly) {
        return "—";
    }
    // A baseline target states the whole rule — "≤ Baseline − 20%" — never a bare
    // "−20%", which reads as a result rather than a commitment. The threshold it
    // comes to is a consequence of this and the Baseline column beside it.
    if (isBaselineToTarget(target)) {
        return formatBaselineRule(target);
    }
    if (target.targetMethod === EnvironmentalTargetMethod.Percentage) {
        return percentAsEntered(target.targetValue);
    }
    const unit = environmentalTargetUnit(target);
    const figure = `${target.targetValue.toLocaleString("en-AU")}${unit ? ` ${unit}` : ""}`;
    // A ceiling reads as one. Without the "≤" a cap and a floor are the same
    // string, and the direction is the whole difference between them.
    return directionForTarget(target) === EnvironmentalTargetDirection.DoNotExceed
        ? `≤ ${figure}`
        : figure;
};
/**
 * A Baseline to target rule in words: `≤ Baseline − 20%` or
 * `≥ Baseline + 10%`. The user types a positive change; the factor supplies
 * the sign, so it is always written out and never implied.
 */
export const formatBaselineRule = (target) => directionForTarget({
    ...target,
    targetMethod: EnvironmentalTargetMethod.BaselineToTarget
}) === EnvironmentalTargetDirection.ReachOrExceed
    ? `≥ Baseline + ${percentAsEntered(target.targetValue)}`
    : `≤ Baseline − ${percentAsEntered(target.targetValue)}`;
/**
 * Field-level validation for the target drawer. Percentage bounds and the
 * required description follow the design; the scope rule enforces unit
 * coherence for quantity-based targets.
 */
export const validateEnvironmentalTarget = (target, 
/**
 * Optional: with the project's commitments the chosen unit is checked
 * against the scope as well. Callers that only have the target (decoders,
 * tests) get the field rules alone.
 */
commitments = []) => {
    const errors = [];
    if (!target.description.trim()) {
        errors.push({
            field: "description",
            message: "Add a short description for this target."
        });
    }
    // Category is optional everywhere; what no target can do is span leaves that
    // disagree on what they measure. Applies to percentage targets as well as
    // fixed values — see `environmentalTargetScopeIsCoherent`. The error lands on
    // the narrowest field the user can act on.
    //
    // This is the only unit rule left. There is no separate denominator check
    // (nothing is measured against a wider scope any more) and no unit check (the
    // unit is derived from this same scope, so it cannot disagree with it).
    if (commitments.length > 0 &&
        !environmentalTargetScopeIsCoherent(target, commitments)) {
        errors.push({
            field: target.category == null ? "category" : "subCategory",
            message: target.category == null
                ? "These categories report in different units - pick one category."
                : "This category reports in more than one unit - select a sub-category."
        });
    }
    if (target.targetMethod === EnvironmentalTargetMethod.Percentage) {
        if (!(target.targetValue > 0 && target.targetValue <= 100)) {
            errors.push({
                field: "targetValue",
                // Zero is excluded deliberately: a 0% threshold is met by definition,
                // so it is a report-only target expressed by mistake.
                message: "Enter a percentage greater than 0 and up to 100."
            });
        }
    }
    if (target.targetMethod === EnvironmentalTargetMethod.FixedValue &&
        !(target.targetValue > 0)) {
        errors.push({
            field: "targetValue",
            message: "Enter a value > 0"
        });
    }
    // A baseline target's `targetValue` is the SIZE OF THE CHANGE, always
    // positive; the factor gives it a sign. A cut of 100% or more is not a cut —
    // it is a commitment to report nothing, or less than nothing — while an
    // increase has no natural ceiling.
    if (isBaselineToTarget(target)) {
        if (target.baselineFactor == null) {
            errors.push({
                field: "baselineFactor",
                message: "Choose a baseline factor."
            });
        }
        else if (target.measure === EnvironmentalTargetMeasure.Emissions &&
            target.baselineFactor !== EnvironmentalTargetDirection.DoNotExceed) {
            errors.push({
                field: "baselineFactor",
                message: "An emissions target can only be ≤ its baseline."
            });
        }
        const increase = target.baselineFactor === EnvironmentalTargetDirection.ReachOrExceed;
        if (!(target.targetValue > 0 && (increase || target.targetValue < 100))) {
            errors.push({
                field: "targetValue",
                message: increase
                    ? "Enter the change as a percentage above 0."
                    : "Enter the change as a percentage between 0 and 100."
            });
        }
        if (!(target.baselineValue != null && target.baselineValue > 0)) {
            errors.push({
                field: "baselineValue",
                message: "Enter the project baseline as a number above zero."
            });
        }
    }
    else {
        // A baseline or factor on any other method is a number nothing reads.
        if (target.baselineValue != null) {
            errors.push({
                field: "baselineValue",
                message: "A baseline applies only to Baseline to target."
            });
        }
        if (target.baselineFactor != null) {
            errors.push({
                field: "baselineFactor",
                message: "A baseline factor applies only to Baseline to target."
            });
        }
    }
    // Measured By: a share is a choice only a Percentage Waste / Water target
    // offers. Any other mismatch is normalised on save
    // (`deriveEnvironmentalTargetMeasuredBy`), so only a share out of place is an
    // error — it would carry picks nothing reads.
    const isShare = target.measuredBy === EnvironmentalTargetMeasuredBy.DestinationShare ||
        target.measuredBy === EnvironmentalTargetMeasuredBy.SourceShare ||
        target.measuredBy === EnvironmentalTargetMeasuredBy.EcoLabelShare;
    if (isShare &&
        !measuredByOptionsForTarget(target).includes(target.measuredBy)) {
        errors.push({
            field: "measuredBy",
            message: "This way of measuring is not available for this target."
        });
    }
    // Value is Materials + Percentage only. Unlike Measured By this is NOT
    // normalised away on save — `deriveEnvironmentalTargetMeasure` would quietly
    // turn the user's ">9% by value" into a tonnage target measuring something
    // else entirely under the description they wrote. Refuse it instead.
    if (target.measure === EnvironmentalTargetMeasure.Value &&
        !measureOptionsForTarget(target).includes(target.measure)) {
        errors.push({
            field: "measure",
            message: "A value target is a percentage of materials — set the type to Materials and the method to Target percentage."
        });
    }
    // Label picks: only an eco-label share reads them. Absent/empty is the
    // wildcard (any approved label), so unlike the destinations on a Destination
    // share there is nothing to require here.
    if ((target.ecoLabels?.length ?? 0) > 0 &&
        target.measuredBy !== EnvironmentalTargetMeasuredBy.EcoLabelShare) {
        errors.push({
            field: "ecoLabels",
            message: "Eco-labels apply only to a target measured by eco-label share."
        });
    }
    // Waste row filters: Reported Quantity Waste targets only. On anything else
    // they would silently drop rows from a figure that never mentions them.
    if (!targetTakesWasteFilters(target)) {
        // Reported on the field that carries the value, so the drawer can show the
        // message under the control the user set rather than under its neighbour.
        if (target.wasteHazardous != null) {
            errors.push({
                field: "wasteHazardous",
                message: "A hazardous filter applies only to a Reported Quantity waste target."
            });
        }
        if ((target.wasteDisposalDestinations?.length ?? 0) > 0) {
            errors.push({
                field: "wasteDisposalDestinations",
                message: "Destinations apply only to a Reported Quantity waste target."
            });
        }
    }
    if (target.measuredBy === EnvironmentalTargetMeasuredBy.DestinationShare &&
        !target.wasteDisposalDestinations?.length) {
        errors.push({
            field: "wasteDisposalDestinations",
            message: "Pick the destinations that count toward the share."
        });
    }
    // Source share picks: required on a Source share, meaningless elsewhere.
    if (target.measuredBy === EnvironmentalTargetMeasuredBy.SourceShare) {
        // A Source share divides the water in scope by where it came from, so the
        // SCOPE has to hold every source. Narrowed to one category it measures
        // that category against itself: 100% when the pick matches the scope, 0%
        // when it does not — the same "true by construction" trap destinations
        // carry on a Destination share, one level up.
        if (target.category != null || target.subCategory != null) {
            errors.push({
                field: target.category == null ? "subCategory" : "category",
                message: "A Source share measures every water category in scope — leave the category as all categories."
            });
        }
        if (!target.shareCategories?.length) {
            errors.push({
                field: "shareCategories",
                message: "Pick the water sources that count toward the share."
            });
        }
        else if (!target.shareCategories.every((c) => SOURCE_SHARE_CATEGORIES.includes(c))) {
            errors.push({
                field: "shareCategories",
                message: "Only withdrawn water categories can count toward the share."
            });
        }
    }
    else if (target.shareCategories?.length) {
        errors.push({
            field: "shareCategories",
            message: "Water sources apply only to a Source share target."
        });
    }
    // Only an emissions target may leave the type out — "total Scope 3" spans
    // three types and so names none. Anything else without a type would be
    // summing litres and tonnes, which is what the coherence rule exists to stop.
    if (target.measure !== EnvironmentalTargetMeasure.Emissions &&
        target.targetType == null) {
        errors.push({
            field: "targetType",
            message: "Choose what this target measures."
        });
    }
    if (target.measure === EnvironmentalTargetMeasure.Emissions) {
        // Two methods, and only two: a figure in tCO₂e, or a change (always a cut)
        // against the project's own baseline. "70% of emissions" has no denominator, and
        // "report only" on a target whose whole point is a threshold says nothing
        // the category library does not already say.
        if (target.targetMethod === EnvironmentalTargetMethod.Percentage ||
            target.targetMethod === EnvironmentalTargetMethod.ReportOnly) {
            errors.push({
                field: "targetValue",
                message: "An emissions target is either a fixed value in tCO₂e or baseline to target."
            });
        }
    }
    else if (target.emissionScopes?.length) {
        // A scope narrows tCO₂e. On a target counted in litres it would silently
        // drop leaves from a figure that never mentions emissions.
        errors.push({
            field: "emissionScopes",
            message: "GHG scopes apply only to a target measured in emissions."
        });
    }
    return errors;
};
